// ===========================================
// Change Clinic Teacher Dialog (admin)
// ===========================================
// Set, change or clear the replacement teacher of ONE 1:1 clinic booking
// (MNM request, 2026-09-06). Booking-level by decision: the per-week
// replacement dialog does not reach clinics and now refuses clinic weeks.
//
// Shared by the three surfaces that edit a booking — the calendar event
// dialog, the student bookings list and the class status grid — so the
// eligibility list, the notify default and the conflict override behave the
// same everywhere.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  useAdminChangeBookingTeacher,
  useEligibleClinicTeachers,
} from '../../../hooks/useHybridBooking';
import { formatUTCDate, parseUTCDate, startOfTodayUTC } from '../../../utils/dateUtils';

const MAX_REASON_LENGTH = 500;

export interface ChangeClinicTeacherDialogProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  lessonId: string;
  /** The teacher the clinic currently RESOLVES to (override ?? lesson teacher). */
  currentTeacherId?: string;
  currentTeacherName?: string;
  /** Clinic week the family thinks of it as (clinicWeekNumber ?? weekNumber). */
  weekNumber?: number;
  /** ISO string or YYYY-MM-DD; a @db.Date value, formatted with UTC getters. */
  scheduledDate?: string;
  startTime?: string;
  endTime?: string;
  /** Called after a successful save (before onClose). */
  onChanged?: () => void;
}

type ConflictError = Error & { response?: { status?: number; data?: { message?: string } } };

export default function ChangeClinicTeacherDialog({
  open,
  onClose,
  bookingId,
  lessonId,
  currentTeacherId,
  currentTeacherName,
  weekNumber,
  scheduledDate,
  startTime,
  endTime,
  onChanged,
}: ChangeClinicTeacherDialogProps) {
  const {
    data: eligibleTeachers = [],
    isLoading: teachersLoading,
    isError: teachersError,
  } = useEligibleClinicTeachers(open ? lessonId : '');
  const changeTeacher = useAdminChangeBookingTeacher();

  // A clinic that has already happened is record-keeping, like the retroactive
  // room change: the server never emails for it, so the switch starts OFF and
  // says why. (Compared at UTC midnight — the @db.Date regime.)
  const isPast = useMemo(
    () => (scheduledDate ? parseUTCDate(scheduledDate) < startOfTodayUTC() : false),
    [scheduledDate]
  );

  // '' = the lesson's regular teacher (the backend stores that as NULL).
  // A current override teacher who has since been DEACTIVATED is not in the
  // eligible list; keep their id as the current value so that choosing the
  // regular teacher still counts as a change (the server accepts the clear).
  const currentValue = useMemo(() => {
    const match = eligibleTeachers.find((t) => t.id === currentTeacherId);
    if (match) return match.isDefault ? '' : match.id;
    return currentTeacherId ?? '';
  }, [eligibleTeachers, currentTeacherId]);
  const regularTeacher = eligibleTeachers.find((t) => t.isDefault);

  // null = "not chosen yet" -> shows the current teacher. Kept separate from the
  // seed so a reason typed while the eligible list is still loading survives.
  const [chosenTeacherId, setChosenTeacherId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [notifyParents, setNotifyParents] = useState(true);
  const [overrideConflicts, setOverrideConflicts] = useState(false);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);

  // Reset ONLY when the dialog opens (not when the eligible list arrives).
  useEffect(() => {
    if (!open) return;
    setChosenTeacherId(null);
    setReason('');
    setNotifyParents(!isPast);
    setOverrideConflicts(false);
    setConflictMessage(null);
  }, [open, isPast]);

  const teacherId = chosenTeacherId ?? currentValue;
  const unchanged = teacherId === currentValue;

  const sessionLabel = useMemo(() => {
    const parts: string[] = [];
    if (weekNumber) parts.push(`Week ${weekNumber}`);
    if (scheduledDate) {
      parts.push(
        formatUTCDate(scheduledDate, { day: '2-digit', month: '2-digit', year: 'numeric' })
      );
    }
    if (startTime && endTime) parts.push(`${startTime}–${endTime}`);
    return parts.join(' · ');
  }, [weekNumber, scheduledDate, startTime, endTime]);

  const handleSave = (force = false) => {
    changeTeacher.mutate(
      {
        id: bookingId,
        data: {
          teacherId: teacherId || null,
          reason: reason.trim() || undefined,
          notifyParents,
          overrideConflicts: force || overrideConflicts,
        },
      },
      {
        onSuccess: () => {
          onChanged?.();
          onClose();
        },
        onError: (error: ConflictError) => {
          if (error.response?.status === 409) {
            setConflictMessage(
              error.response.data?.message ?? 'The teacher is not available at this time.'
            );
          }
        },
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Change clinic teacher</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {sessionLabel && (
            <Typography variant="body2" color="text.secondary">
              {sessionLabel}
              {currentTeacherName ? ` · currently ${currentTeacherName}` : ''}
            </Typography>
          )}
          <Alert severity="info">
            Changes the teacher for this one 1:1 session only. The date, time and room stay the
            same.
          </Alert>

          <FormControl fullWidth size="small" disabled={teachersLoading || changeTeacher.isPending}>
            {/* ⚠️ `shrink` is REQUIRED here. `displayEmpty` + `renderValue` mean this
                Select ALWAYS paints a value (the regular teacher's name when nothing
                is chosen), but MUI only auto-shrinks the label when `value` is
                truthy — and the "regular teacher" option is deliberately the empty
                string. Without this the label sits ON TOP of the teacher name
                (Andrew, staging walkthrough 2026-09-08). `notched` cuts the gap in
                the outline to match; without it the label overlaps the border. */}
            <InputLabel id="clinic-teacher-select-label" shrink>Teacher</InputLabel>
            <Select
              labelId="clinic-teacher-select-label"
              label="Teacher"
              value={teacherId}
              displayEmpty
              notched
              onChange={(e) => {
                setChosenTeacherId(e.target.value as string);
                setConflictMessage(null);
                setOverrideConflicts(false);
              }}
              renderValue={(value) => {
                if (!value) return regularTeacher ? `${regularTeacher.name} (regular teacher)` : 'Regular teacher';
                // The current override may be absent from the list (deactivated) - name them anyway.
                return eligibleTeachers.find((t) => t.id === value)?.name ?? currentTeacherName ?? '';
              }}
              inputProps={{ 'aria-label': 'Teacher' }}
            >
              {eligibleTeachers.map((t) => (
                <MenuItem key={t.id} value={t.isDefault ? '' : t.id}>
                  {t.isDefault ? `${t.name} (regular teacher)` : t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Three states, never collapsed: loading, failed, genuinely empty. */}
          {teachersError ? (
            <Alert severity="error">Could not load the teacher list. Close and try again.</Alert>
          ) : (
            !teachersLoading &&
            eligibleTeachers.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No other active teacher teaches this instrument.
              </Typography>
            )
          )}

          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
            fullWidth
            multiline
            rows={2}
            size="small"
            helperText={reason.length > 0 ? `${reason.length}/${MAX_REASON_LENGTH}` : 'Included in the family email'}
          />

          <FormControlLabel
            control={
              <Switch
                checked={notifyParents}
                onChange={(e) => setNotifyParents(e.target.checked)}
                disabled={isPast}
              />
            }
            label="Notify parents via email"
          />
          {isPast && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              This clinic has already happened, so this only corrects the record. No email will be
              sent.
            </Typography>
          )}

          {conflictMessage && (
            <Alert severity="warning">
              {conflictMessage}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                You can save anyway if this has been arranged with the teacher.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={changeTeacher.isPending}>
          Cancel
        </Button>
        {conflictMessage ? (
          <Button
            variant="contained"
            color="warning"
            onClick={() => handleSave(true)}
            disabled={changeTeacher.isPending}
          >
            {changeTeacher.isPending ? <CircularProgress size={20} /> : 'Save anyway'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => handleSave(false)}
            disabled={unchanged || teachersLoading || teachersError || changeTeacher.isPending}
          >
            {changeTeacher.isPending ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
